import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import '../css/Reports.css'; 

// --- SVG Icons ---
const SearchIcon = () => (
    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
);

const SaveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

const EmptyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);


// --- UI Components ---
const LoadingSpinner = () => (
    <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading Reports...</p>
    </div>
);

const EmptyState = () => (
    <div className="empty-state-container">
        <EmptyIcon />
        <h3>No Reports Found</h3>
        <p>There are no student reports assigned to you at the moment.</p>
    </div>
);

const Reports = () => {
    const [mentorEmail, setMentorEmail] = useState('');
    const [mentorName, setMentorName] = useState('');
    const [reports, setReports] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filteredReports, setFilteredReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const email = Cookies.get('Mentorsemail');
        const role = Cookies.get('role'); // <--- get user role
        if (email) {
            setMentorEmail(email);
            setUserRole(role); // <--- set role
            fetchMentorName(email);
        } else {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (mentorName) {
                setIsLoading(true);
                const students = await fetchStudentProfiles();
                await fetchReports(students);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [mentorName]);

    useEffect(() => {
        const filtered = reports.filter((report) =>
            report.Student_Name?.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredReports(filtered);
    }, [searchText, reports]);

    const fetchMentorName = async (email) => {
        try {
            const res = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/read.php');
            const data = await res.json();
            const mentor = data.find((m) => m.Email === email);
            if (mentor) {
                setMentorName(mentor.Full_Name);
            }
        } catch (error) {
            console.error('Error fetching mentor name:', error);
        }
    };

    const fetchStudentProfiles = async () => {
        try {
            const res = await fetch('https://ethiccraft.org/backend/profile/read.php');
            const data = await res.json();
            return mentorName ? data.filter((s) => s.Mentors_Name === mentorName) : [];
        } catch (error) {
            console.error('Error fetching student profiles:', error);
            return [];
        }
    };

    const fetchReports = async (students) => {
        try {
            const res = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/Read_Report.php');
            const data = await res.json();
            const reportsMap = new Map(data.map(r => [r.Student_ID, r]));
            
            const merged = students.map((student) => ({
                Student_ID: student.user_id,
                Student_Name: student.full_name,
                Student_Photo: student.profile_image,
                Chanting: reportsMap.get(student.user_id)?.Chanting || '',
                Sincerity: reportsMap.get(student.user_id)?.Sincerity || '',
                Need: reportsMap.get(student.user_id)?.Need || '',
                Interest: reportsMap.get(student.user_id)?.Interest || '',
                Concern: reportsMap.get(student.user_id)?.Concern || '',
                Remarks: reportsMap.get(student.user_id)?.Remarks || '',
            }));
            setReports(merged);
        } catch (error) {
            console.error('Error fetching report data:', error);
        }
    };

    const handleInputChange = (studentId, field, value) => {
        const updatedReports = reports.map(r => 
            r.Student_ID === studentId ? { ...r, [field]: value } : r
        );
        setReports(updatedReports);
    };

    const handleSave = async (report) => {
        const result = await Swal.fire({
            title: 'Confirm Changes',
            text: `Are you sure you want to save the report for ${report.Student_Name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, save it!',
            customClass: {
                popup: 'swal2-popup',
                title: 'swal2-title',
                htmlContainer: 'swal2-html-container',
                confirmButton: 'swal2-confirm',
                cancelButton: 'swal2-cancel'
            }
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/Update_Report.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(report),
            });

            const resData = await response.json();

            if (resData.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: resData.message,
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                throw new Error(resData.message || 'Failed to update report.');
            }
        } catch (error) {
            console.error('Update error:', error);
            Swal.fire({
                icon: 'error',
                title: 'An Error Occurred',
                text: `Could not save the report. Details: ${error.message}`,
            });
        }
    };

    return (
        <div className="main">
            {userRole === 'facilitator' && (
            <div className="data-container">
                <div className="container-header">
                    <h1 className="header-title">Student Reports</h1>
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            className="search-input"
                            type="text"
                            placeholder="Search by student name..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-wrapper">
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : filteredReports.length > 0 ? (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Chanting</th>
                                    <th>Sincerity</th>
                                    <th>Need</th>
                                    <th>Interest</th>
                                    <th>Concern</th>
                                    <th>Remarks</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredReports.map((report) => (
                                    <tr key={report.Student_ID}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <img
                                                    src={report.Student_Photo ? `https://ethiccraft.org/${report.Student_Photo}` : `https://ui-avatars.com/api/?name=${report.Student_Name}&background=random`}
                                                    alt={report.Student_Name}
                                                    className="profile-image-small"
                                                />
                                                <span>{report.Student_Name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={report.Chanting}
                                                onChange={(e) => handleInputChange(report.Student_ID, 'Chanting', e.target.value)}
                                                className="input-cell"
                                            />
                                        </td>
                                        <td>
                                            <input type="text" value={report.Sincerity} onChange={(e) => handleInputChange(report.Student_ID, 'Sincerity', e.target.value)} className="input-cell" />
                                        </td>
                                        <td>                                           
                                            <input type="text" value={report.Need} onChange={(e) => handleInputChange(report.Student_ID, 'Need', e.target.value)} className="input-cell" />                                           
                                        </td>
                                        <td>                                           
                                            <input type="text" value={report.Interest} onChange={(e) => handleInputChange(report.Student_ID, 'Interest', e.target.value)} className="input-cell" />                                           
                                        </td>
                                        <td>                                           
                                            <input type="text" value={report.Concern} onChange={(e) => handleInputChange(report.Student_ID, 'Concern', e.target.value)} className="input-cell" />                                           
                                        </td>
                                        <td>                                           
                                            <input type="text" value={report.Remarks} onChange={(e) => handleInputChange(report.Student_ID, 'Remarks', e.target.value)} className="input-cell" />                                           
                                        </td>
                                        <td>                                           
                                            <button onClick={() => handleSave(report)} className="btn btn-save">
                                                <SaveIcon />
                                                Save
                                            </button>                                           
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <EmptyState />
                    )}
                </div>
            </div>
        )}
        </div>
    );
};

export default Reports;
